// app_client/src/pages/admin/ProductDetail.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, History, PackageSearch } from 'lucide-react';
import { toast } from 'react-hot-toast';

import useProductStore from '../../store/useProductStore';
import ProductForm from '../../components/products/ProductForm';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  // Store'dan suggestProductCode (veya senin isimlendirmenle benzeri) fonksiyonunu çekiyoruz
  const { 
    fetchProductById, 
    saveProduct, 
    checkCodeAvailability, 
    suggestProductCode 
  } = useProductStore();

  const [loading, setLoading] = useState(false);
  const [isReady, setIsReady] = useState(false); 
  const [isCodeTaken, setIsCodeTaken] = useState(false);
  
  const [formData, setFormData] = useState({ 
    name: '', 
    type: 'Material', 
    code: '', 
    category: '',
    purchasePrice: 0,
    purchaseVatRate: 20,
    purchaseVatIncluded: false,
    purchaseCurrency: 'TRY',
    salePrice: 0, 
    saleVatRate: 20,
    saleVatIncluded: false,
    saleCurrency: 'TRY',
    unit: 'Adet',
    stockQuantity: 0,
    minStockLevel: 5, 
    description: '',
    barcode: '',
    isActive: true
  });

  // Yeni kod önerisi alan yardımcı fonksiyon
  const getSuggestedCode = useCallback(async (type) => {
    try {
      const suggested = await suggestProductCode(type);
      if (suggested) {
        setFormData(prev => ({ ...prev, code: suggested }));
        setIsCodeTaken(false);
      }
    } catch (err) {
      console.error("Kod önerisi alınamadı:", err);
    }
  }, [suggestProductCode]);

  // Sayfa İlk Açılış Ayarları
  useEffect(() => {
    const init = async () => {
      if (isNew) {
        // İlk açılışta default tip (Material) için backend'den kod iste
        await getSuggestedCode('Material');
        setIsReady(true);
      } else if (id) {
        const data = await fetchProductById(id);
        if (data) {
          setFormData({ ...data });
          setIsReady(true);
        }
      } else {
        setIsReady(true);
      }
    };
    init();
  }, [id, isNew, fetchProductById, getSuggestedCode]);

  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'stockQuantity') return;

    let val;
    if (type === 'checkbox') {
      val = checked;
    } else if (type === 'number') {
      val = Number(value);
    } else if (value === 'true' || value === 'false') {
      val = value === 'true';
    } else {
      val = value;
    }

    // Tür değiştiğinde backend'den yeni öneri al (Sadece yeni kayıtta)
    if (name === 'type' && isNew) {
      setFormData(prev => ({ ...prev, type: val }));
      await getSuggestedCode(val);
      return;
    }

    setFormData(prev => ({ ...prev, [name]: val }));
  };

  const handleCodeCheck = async (code) => {
    const trimmedCode = code?.trim();
    if (!trimmedCode || trimmedCode.length < 3) return;

    const productId = isNew ? null : id;
  
    try {
      const taken = await checkCodeAvailability(trimmedCode, productId);
      setIsCodeTaken(taken);
      if (taken) toast.error("Bu ürün kodu zaten kullanımda!");
    } catch (error) {
      console.error("Kod kontrolü hatası:", error);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (isCodeTaken) return toast.error("Lütfen benzersiz bir ürün kodu girin.");
    
    setLoading(true);
    const res = await saveProduct(formData, isNew ? null : id);
    
    if (res.success) {
      toast.success(isNew ? "Ürün başarıyla oluşturuldu" : "Değişiklikler kaydedildi");
      if (isNew) navigate(`/products/${res.data._id}`);
    } else {
      toast.error(res.message);
    }
    setLoading(false);
  };

  if (!isReady) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 text-slate-500">
        <Loader2 className="animate-spin text-blue-600" size={40} />
        <p className="font-bold tracking-tight">Ürün verileri hazırlanıyor...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Üst Bar */}
      <div className="flex items-center justify-between bg-white p-4 rounded-4xl border border-slate-100 shadow-sm">
        <button onClick={() => navigate('/product-management')} className="group flex items-center gap-2 font-bold text-slate-500 hover:text-slate-900 px-4 py-2 hover:bg-slate-50 rounded-2xl transition-all">
          <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" /> Ürün Listesi
        </button>
        {!isNew && (
          <div className="flex items-center gap-2 px-4">
            <span className={`w-2.5 h-2.5 rounded-full ${formData.isActive ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`} />
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
              {formData.isActive ? 'Sistemde Aktif' : 'Pasif Durumda'}
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <ProductForm 
            formData={formData} 
            handleChange={handleChange}
            handleUpdate={handleUpdate}
            loading={loading}
            isNew={isNew}
            isCodeTaken={isCodeTaken}
            onCodeBlur={(e) => handleCodeCheck(e.target.value)}
            stockQuantityDisabled={true} 
          />
        </div>

        <div className="lg:col-span-4 space-y-6">
          {!isNew && (
            <div className="bg-white border border-slate-100 rounded-[2.5rem] p-8 space-y-6 shadow-sm relative overflow-hidden">
               <div className="absolute -right-4 -top-4 text-slate-50/50 rotate-12">
                  <PackageSearch size={120} />
               </div>
               <div className="relative z-10">
                 <h5 className="text-[10px] font-black uppercase text-slate-400 tracking-widest border-b border-slate-50 pb-4 mb-4">Stok Özeti</h5>
                 <div className="flex items-baseline gap-2">
                    <span className={`text-5xl font-black tracking-tighter ${formData.stockQuantity <= formData.minStockLevel ? 'text-rose-600' : 'text-slate-800'}`}>
                      {formData.stockQuantity}
                    </span>
                    <span className="text-slate-400 font-bold uppercase text-xs">{formData.unit}</span>
                 </div>
                 <div className="mt-8 space-y-3">
                    <div className="flex justify-between items-center p-3 bg-slate-50 rounded-2xl text-xs">
                        <span className="font-bold text-slate-500">Kritik Eşik:</span>
                        <span className="font-black text-slate-800">{formData.minStockLevel} {formData.unit}</span>
                    </div>
                    <div className={`text-[10px] font-black text-center py-2 rounded-full border ${formData.stockQuantity > formData.minStockLevel ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-rose-50 text-rose-600 border-rose-100'}`}>
                      {formData.stockQuantity > formData.minStockLevel ? 'STOK YETERLİ' : 'KRİTİK SEVİYE UYARISI'}
                    </div>
                 </div>
               </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
